// libraries
import { createContext, useContext, useMemo } from "react";
import { I18N_KEY, useLocalStorage } from "../hooks/useLocalStorage";

// types
type LanguageContextType = {
  setLanguage: React.Dispatch<React.SetStateAction<string>>;
  language: string;
};

type LanguageProviderProps = {
  initialValues: {
    language: string;
  };
};

export const LanguageContext = createContext<LanguageContextType>({
  setLanguage: () => null,
  language: "en",
});

export const LanguageProvider: React.FC<
  React.PropsWithChildren<LanguageProviderProps>
> = ({ children, initialValues }) => {
  const [language, setLanguage] = useLocalStorage(
    I18N_KEY,
    initialValues.language,
  );

  /**
   * Memoize provider value to reduce likelihood of re-render bug
   */
  const memoizedLanguageProviderValue = useMemo(
    () => ({
      setLanguage,
      language,
    }),
    [setLanguage, language],
  );

  return (
    <LanguageContext.Provider value={memoizedLanguageProviderValue}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguageContext = (): LanguageContextType => {
  return useContext(LanguageContext);
};
